import {
	ApiUser,
	isAdminUser,
	isEmployeeUser,
	isStudentUser,
} from 'auth/token';

import {
	CommunityOfferListDto,
	CommunityOfferType,
} from 'typing/communityOffers';

export type CommunityOfferCapabilities = {
	view: boolean;
	create: boolean;
	edit: boolean;
	allowedTypes: CommunityOfferType[];
};

export const getCommunityOfferCapabilities = (
	user?: ApiUser,
): CommunityOfferCapabilities => {
	if (isAdminUser(user) || isEmployeeUser(user)) {
		return {
			view: true,
			create: true,
			edit: true,
			allowedTypes: ['BRIGADE', 'TUTORING'],
		};
	}
	if (isStudentUser(user)) {
		return {
			view: true,
			create: true,
			edit: true,
			allowedTypes: ['TUTORING'],
		};
	}
	return { view: false, create: false, edit: false, allowedTypes: [] };
};

export const canEditCommunityOffer = (
	user: ApiUser | undefined,
	offer: Pick<CommunityOfferListDto, 'author' | 'type' | 'isVisible'>,
) =>
	isAdminUser(user) ||
	(user?.id === offer.author.id &&
		(isEmployeeUser(user) ||
			(isStudentUser(user) && offer.type === 'TUTORING' && !offer.isVisible)));

export type CommunityOfferWorkflowState = Pick<
	CommunityOfferListDto,
	'author' | 'isVisible'
> & { readonly approvalRequested: boolean };

export const canSubmitCommunityOffer = (
	user: ApiUser | undefined,
	offer: CommunityOfferWorkflowState,
) =>
	getCommunityOfferCapabilities(user).edit &&
	user?.id === offer.author.id &&
	!offer.isVisible;

export const canActivateCommunityOffer = (
	user: ApiUser | undefined,
	offer: CommunityOfferWorkflowState,
) =>
	!offer.isVisible &&
	((isEmployeeUser(user) && user.id === offer.author.id) ||
		(isAdminUser(user) &&
			(user.id === offer.author.id ||
				!['STUDENT', 'ALUMNUS'].includes(offer.author.personType) ||
				offer.approvalRequested === true)));

export const canApproveAndPublishCommunityOffer = (
	user: ApiUser | undefined,
	offer: CommunityOfferWorkflowState,
) => isAdminUser(user) && canActivateCommunityOffer(user, offer);

export const canDeactivateCommunityOffer = (
	user: ApiUser | undefined,
	offer: CommunityOfferWorkflowState,
) =>
	(offer.isVisible || offer.approvalRequested === true) &&
	(isAdminUser(user) ||
		((isEmployeeUser(user) || isStudentUser(user)) &&
			user?.id === offer.author.id));

export const canCreateCommunityOfferResponse = (
	user: ApiUser | undefined,
	offer: Pick<
		CommunityOfferListDto,
		'author' | 'type' | 'isVisible' | 'validFrom' | 'validTo'
	>,
	today?: string,
) => {
	const now = new Date();
	const localToday =
		today ??
		`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
			2,
			'0',
		)}-${String(now.getDate()).padStart(2, '0')}`;
	return (
		(isStudentUser(user) ||
			(isEmployeeUser(user) && offer.type === 'TUTORING')) &&
		user?.id !== offer.author.id &&
		offer.isVisible === true &&
		!!offer.validFrom &&
		!!offer.validTo &&
		offer.validFrom <= localToday &&
		localToday <= offer.validTo
	);
};

export const canViewOwnCommunityOfferResponses = (user?: ApiUser) =>
	isAdminUser(user) || isStudentUser(user) || isEmployeeUser(user);

export const canViewOwnCommunityOfferTopicSuggestions = (user?: ApiUser) => {
	const capabilities = getCommunityOfferCapabilities(user);
	return capabilities.create && capabilities.allowedTypes.includes('TUTORING');
};

export const canViewCommunityOfferResponsesRoute = (user?: ApiUser) =>
	isAdminUser(user) || isEmployeeUser(user) || isStudentUser(user);

export const canViewCommunityOfferResponses = (
	user: ApiUser | undefined,
	authorId: string,
) =>
	isAdminUser(user) ||
	((isEmployeeUser(user) || isStudentUser(user)) && user?.id === authorId);
